import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import React from 'react';

import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

/**
 * Palette pulled from the redesign mock so the sidebar matches it faithfully. These are
 * intentionally literal (not theme tokens) to track the mock exactly while it's behind the flag.
 */
const TEXT_COLOR = '#1B1B1F';
const HOVER_BLUE = '#3C65FB';
const ACTIVE_BG = '#E8EDFF';

const useStyles = createStyles(() => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    minWidth: 0,
    padding: '3px 4px',
    borderRadius: 6,
    cursor: 'pointer',
    color: TEXT_COLOR,
    lineHeight: '20px',
  },
  rowHoverHighlight: {
    '&:hover': {
      color: HOVER_BLUE,
    },
  },
  rowActive: {
    backgroundColor: ACTIVE_BG,
  },
  icon: {
    flex: 'none',
    width: 14,
    height: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconDimmed: {
    opacity: 0.5,
  },
  label: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  trailing: {
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    // Pin to the row's line box so a taller hover control (the "…" overflow button, which is a couple
    // pixels taller than the text) overflows visually instead of growing the row's height.
    height: 20,
  },
}));

export interface SidebarRowProps {
  icon: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Optional right-aligned content (e.g. an overflow menu), typically revealed on hover. */
  trailing?: React.ReactNode;
  /** Chat rows dim the icon (greyer); nav items keep it near-black. */
  dimIcon?: boolean;
  /** Nav items turn blue on hover; chat rows do not. */
  hoverHighlight?: boolean;
  /** Selected chat rows get a subtle background. */
  active?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * The single row primitive used for both the sidebar nav items (New Chat / Agents / ...) and the
 * Starred / Recent chat rows, so they align and share styling. Mirrors the redesign mock: 14px icon
 * slot, 9px gap, 3px/4px padding, 13px label with ellipsis.
 */
export default function SidebarRow({
  icon,
  label,
  onClick,
  onMouseEnter,
  onMouseLeave,
  trailing,
  dimIcon = false,
  hoverHighlight = false,
  active = false,
  dataTestAttrs,
}: SidebarRowProps): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cx(classes.row, hoverHighlight && classes.rowHoverHighlight, active && classes.rowActive)}
      {...getActionButtonRoleProps({onPress: onClick})}
      {...(dataTestAttrs ? getTestProps(dataTestAttrs) : {})}
    >
      <span className={cx(classes.icon, dimIcon && classes.iconDimmed)}>{icon}</span>
      <span className={classes.label}>{label}</span>
      {trailing != null && <span className={classes.trailing}>{trailing}</span>}
    </div>
  );
}
